import { useCallback, useMemo, useRef, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import css from './views.module.css'
import { Card, Chip, ViewHeader, QueryError, Button, Notice } from '../ui'
import { useApprovals, useDepartments, APPROVALS_LIMIT } from '../data/queries'
import { useDecide } from '../data/useDecide'
import {
  toQueueItem, readableKind, isOpen, isAutomated, verbsFor,
  CLOSE_STATE, DISMISS_STATE, REOPEN_STATE,
  type DecisionState, type QueueItem,
} from '../data/map/approvals'
import { departmentsFrom } from '../data/map/department'
import { useQueueKeys } from '../app/useQueueKeys'
import { RunTrace } from './RunTrace'
import { age } from '../lib/time'
import { short } from '../lib/text'

/** Rotates by day. Quiet, not celebratory -- an empty queue is the normal state. */
const EMPTY_LINES = [
  'Nothing open. The robots are unsupervised. Godspeed.',
  'Nothing open. The fleet is getting on with it.',
  'Empty. Someone has been working through it.',
  'Nothing here is waiting on a person.',
  'All clear. It will fill up again on its own.',
]

/**
 * The queue is triage, not authorisation: closing an item authorises nothing,
 * it records that a human has dealt with whatever the item was about. The verbs
 * say that, and nothing here claims an effect on the fleet.
 */

export function Approvals() {
  const approvals = useApprovals()
  const depts = useDepartments()
  const decide = useDecide()
  const [params, setParams] = useSearchParams()
  const dept = params.get('dept') ?? 'All'
  // Narrowing to one producer, which is how the cost strip's fleet line lands
  // on its own findings. Any source value works -- the column is data, the
  // same as the department beside it, and nothing here knows the strings.
  const source = params.get('source')
  const traceId = params.get('trace')

  const [focusIdx, setFocusIdx] = useState(0)
  const [noteOpenFor, setNoteOpenFor] = useState<string | null>(null)
  const [dismissPromptFor, setDismissPromptFor] = useState<string | null>(null)
  const [drafts, setDrafts] = useState<Record<string, string>>({})
  const [failure, setFailure] = useState<{ id: string; message: string } | null>(null)
  const noteInput = useRef<HTMLInputElement>(null)

  const items = useMemo(
    () => (approvals.data ?? []).map((row) => toQueueItem(row)),
    [approvals.data],
  )
  const departments = useMemo(
    () => departmentsFrom(depts.data ?? [], approvals.data ?? []),
    [depts.data, approvals.data],
  )
  const visible = useMemo(
    () =>
      items.filter(
        (item) => (dept === 'All' || item.department === dept) && (source === null || item.source === source),
      ),
    [items, dept, source],
  )
  // The empty state fires on pending count, not list length: a queue of six
  // decided items is still a cleared queue.
  const openCount = visible.filter(isOpen).length
  const hasClosed = visible.some((item) => !isOpen(item))
  // If the fetch came back full, older items were cut off. Say so rather than
  // letting the list quietly misrepresent what is waiting.
  const atLimit = (approvals.data?.length ?? 0) >= APPROVALS_LIMIT
  const focused = visible[Math.min(focusIdx, visible.length - 1)]
  const traceItem = traceId ? (items.find((item) => item.id === traceId) ?? null) : null

  const setParam = useCallback(
    (key: string, value: string | null) => {
      const next = new URLSearchParams(params)
      if (!value || value === 'All') next.delete(key)
      else next.set(key, value)
      setParams(next, { replace: true })
    },
    [params, setParams],
  )

  const run = useCallback(
    (item: QueueItem | undefined, state: DecisionState) => {
      if (!item) return
      setFailure(null)
      decide.mutate(
        { id: item.id, state, note: state === 'pending' ? null : drafts[item.id] },
        {
          onError: (error) => setFailure({ id: item.id, message: error.message }),
          onSuccess: () => {
            setNoteOpenFor(null)
            setDismissPromptFor(null)
          },
        },
      )
    },
    [decide, drafts],
  )

  useQueueKeys(
    {
      next: () => setFocusIdx((i) => Math.min(i + 1, Math.max(visible.length - 1, 0))),
      previous: () => setFocusIdx((i) => Math.max(i - 1, 0)),
      close: () => run(focused, CLOSE_STATE),
      dismiss: () => {
        // Dismissing is where a note earns its place: it is what stops the
        // same finding being re-litigated next week. So it prompts rather
        // than firing, and the prompt is one deliberate extra click.
        if (focused) setDismissPromptFor(focused.id)
      },
      note: () => {
        if (!focused) return
        setNoteOpenFor(focused.id)
        window.setTimeout(() => noteInput.current?.focus(), 0)
      },
      reopen: () => { if (focused && !isOpen(focused)) run(focused, REOPEN_STATE) },
      openTrace: () => { if (focused) setParam('trace', focused.id) },
      escape: () => {
        if (traceId) setParam('trace', null)
        else if (dismissPromptFor) setDismissPromptFor(null)
        else if (noteOpenFor) setNoteOpenFor(null)
        else (document.activeElement as HTMLElement | null)?.blur()
      },
    },
    { enabled: true, overlayOpen: traceId !== null },
  )

  if (approvals.error) return <QueryError error={approvals.error} />

  return (
    <>
      <ViewHeader
        title="Inbox"
        aside={
          approvals.data
            ? `${openCount} open${dept === 'All' ? '' : ` in ${dept}`}${source ? ` from ${source}` : ''}`
            : undefined
        }
      />

      <div className={css.filters} style={{ marginBottom: 14 }}>
        <button
          className={dept === 'All' ? `${css.filterPill} ${css.filterActive}` : css.filterPill}
          onClick={() => setParam('dept', 'All')}
        >
          All departments
        </button>
        {departments.map((d) => (
          <button
            key={d.slug}
            className={dept === d.slug ? `${css.filterPill} ${css.filterActive}` : css.filterPill}
            onClick={() => setParam('dept', d.slug)}
          >
            {d.label}
          </button>
        ))}
        {/* Only present when a link narrowed the queue -- there is no row of
            source pills, because a source is where an item came from rather
            than a lens someone picks. It shows what is hidden and how to stop
            hiding it. */}
        {source ? (
          <button
            className={`${css.filterPill} ${css.filterActive}`}
            onClick={() => setParam('source', null)}
            title="Show items from every source"
          >
            {source} ✕
          </button>
        ) : null}
      </div>

      <Card>
        <div className={css.queue}>
          {visible.map((item, index) => (
              <QueueRow
                key={item.id}
                item={item}
                focused={index === focusIdx}
                onFocus={() => setFocusIdx(index)}
                noteOpen={noteOpenFor === item.id}
                dismissPrompt={dismissPromptFor === item.id}
                onDismissPrompt={() => setDismissPromptFor(item.id)}
                onCancelDismiss={() => setDismissPromptFor(null)}
                draft={drafts[item.id] ?? ''}
                onDraft={(value) => setDrafts((d) => ({ ...d, [item.id]: value }))}
                onToggleNote={() => setNoteOpenFor((current) => (current === item.id ? null : item.id))}
                onDecide={(state) => run(item, state)}
                onTrace={() => setParam('trace', item.id)}
                busy={decide.isPending}
                error={failure?.id === item.id ? failure.message : null}
                noteRef={noteOpenFor === item.id ? noteInput : undefined}
              />
          ))}

          {/* Fires on the open count, not the list length: a queue of six
              closed items is a cleared queue, and saying so is the whole
              point of the view. The closed rows stay above it until they
              age out overnight. */}
          {openCount === 0 ? (
            <div style={{ padding: '48px 20px', textAlign: 'center' }}>
              <div className={`${css.emptyFaces} mono`}>[o_o] [o_-] [-_o] [u_u]</div>
              <p style={{ fontSize: 14, color: 'var(--ink-3)', maxWidth: '46ch', margin: '0 auto', lineHeight: 1.55 }}>
                {EMPTY_LINES[new Date().getDate() % EMPTY_LINES.length]}
              </p>
              {hasClosed ? (
                <p style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 10 }}>
                  Decided items above drop off overnight.
                </p>
              ) : null}
            </div>
          ) : null}
        </div>
      </Card>

      {atLimit ? (
        <Notice>
          Showing the first {APPROVALS_LIMIT} items. There are more open than this view fetches —
          that should not happen, and is worth looking at rather than scrolling past.
        </Notice>
      ) : hasClosed ? (
        <Notice>
          Closed and dismissed items drop off after midnight; they stay in the activity feed, with
          the note.
        </Notice>
      ) : null}

      <div className={css.qFooter}>
        Closing an item records that a person dealt with it. It does not merge, deploy or
        authorise anything — the fleet's work ends at a pull request. Every close and dismissal is
        written to the activity feed with your name and note, by the database rather than by this
        page.
        <div style={{ marginTop: 6 }}>
          <kbd>j</kbd> <kbd>k</kbd> move · <kbd>c</kbd> close · <kbd>x</kbd> dismiss ·{' '}
          <kbd>n</kbd> note · <kbd>u</kbd> reopen · <kbd>↵</kbd> run trace · <kbd>Esc</kbd> back
        </div>
      </div>

      <RunTrace item={traceItem} onClose={() => setParam('trace', null)} />
    </>
  )
}

function QueueRow(props: {
  item: QueueItem
  focused: boolean
  onFocus: () => void
  noteOpen: boolean
  dismissPrompt: boolean
  onDismissPrompt: () => void
  onCancelDismiss: () => void
  draft: string
  onDraft: (value: string) => void
  onToggleNote: () => void
  onDecide: (state: DecisionState) => void
  onTrace: () => void
  busy: boolean
  error: string | null
  noteRef?: React.RefObject<HTMLInputElement>
}) {
  const { item } = props
  const open = isOpen(item)
  const kind = readableKind(item.kind)
  const verbs = verbsFor(item)

  return (
    <div
      className={[
        css.qRow,
        props.focused ? css.qRowFocused : '',
        open ? '' : css.qRowDecided,
      ].filter(Boolean).join(' ')}
      onClick={props.onFocus}
    >
      <div className={css.qTags}>
        <Chip>{item.department}</Chip>
        {kind ? <Chip neutral>{kind}</Chip> : null}
        <span className={`${css.qSource} mono`}>{item.source}</span>
        <span className={css.qAge}>{item.age}</span>
      </div>

      <div className={css.qTitle}>{item.title}</div>
      {item.summary ? <div className={css.qSummary}>{item.summary}</div> : null}
      {/* The plain-language sentence, cut to a row's worth. Rule 3 again: the
          whole of it is in the detail view, and this is the queue. */}
      {item.plain ? <div className={css.qPlain}>{short(item.plain, 180)}</div> : null}
      {item.headline ? <div className={`${css.qHeadline} mono`}>{item.headline}</div> : null}

      {item.ticketRef || item.buildBranch ? (
        <div className={css.qTicket}>
          {item.ticketRef ? <span className={`${css.qTicketKey} mono`}>{item.ticketRef}</span> : null}
          {item.repo ? <span className={css.qMeta}>{item.repo}</span> : null}
          {item.buildBranch ? <span className={`${css.qMeta} mono`}>{item.buildBranch}</span> : null}
          {/* An attempt above 1 means the build was retried before it got here,
              which is context a person deciding it should not have to dig for. */}
          {item.attempt !== null && item.attempt > 1 ? (
            <span className={css.qMeta}>attempt {item.attempt}</span>
          ) : null}
        </div>
      ) : null}

      <div className={css.qLinks}>
        {/* Neutral: not every item is a review. */}
        <Link to={`/inbox/${item.id}`}>Open</Link>
        {item.refUrl ? <a href={item.refUrl} target="_blank" rel="noreferrer">{verbs.refLink}</a> : null}
        {item.ticketUrl ? <a href={item.ticketUrl} target="_blank" rel="noreferrer">Issue</a> : null}
        <button
          onClick={props.onTrace}
          style={{ background: 'none', border: 'none', padding: 0, font: 'inherit', color: 'var(--brand-ink)', cursor: 'pointer' }}
        >
          Run trace
        </button>
      </div>

      {open ? (
        <div className={css.qActions}>
          {/* Filled on the focused row only: one primary action per context. */}
          <Button variant={props.focused ? 'primary' : 'default'} disabled={props.busy} onClick={() => props.onDecide(CLOSE_STATE)}>
            {verbs.close}
          </Button>
          <Button variant="quiet" disabled={props.busy} onClick={props.onDismissPrompt}>{verbs.dismiss}</Button>
          <Button disabled={props.busy} onClick={props.onToggleNote}>Note</Button>
        </div>
      ) : (
        <div className={css.qDecided}>
          {/* A rule that auto-cleared an item is a decider, but it is not a
              person, and the record should not read as though someone sat and
              considered it. */}
          <span>
            {verbs.decided} ·{' '}
            {isAutomated(item)
              ? `automatically, by ${(item.decidedBy ?? '').replace(/^system:/, '')}`
              : (item.decidedBy ?? 'you')}{' '}
            · {item.decidedAt ? age(item.decidedAt) : 'just now'}
          </span>
          <Button variant="quiet" disabled={props.busy} onClick={() => props.onDecide(REOPEN_STATE)}>Reopen</Button>
        </div>
      )}

      {props.dismissPrompt && open ? (
        <div className={css.qNoteRow}>
          <input
            ref={props.noteRef}
            className={css.input}
            autoFocus
            value={props.draft}
            placeholder={verbs.dismissPrompt}
            onChange={(event) => props.onDraft(event.target.value)}
            aria-label="Reason for dismissing"
          />
          <Button variant="primary" disabled={props.busy} onClick={() => props.onDecide(DISMISS_STATE)}>{verbs.dismiss}</Button>
          <Button variant="quiet" disabled={props.busy} onClick={props.onCancelDismiss}>Cancel</Button>
        </div>
      ) : null}

      {props.noteOpen && open && !props.dismissPrompt ? (
        <div className={css.qNoteRow}>
          <input
            ref={props.noteRef}
            className={css.input}
            value={props.draft}
            placeholder={item.ticketRef ? `Note — kept with the decision on ${item.ticketRef}` : 'Short note for the record (optional)'}
            onChange={(event) => props.onDraft(event.target.value)}
            aria-label="Decision note"
          />
          <Button onClick={props.onToggleNote}>Save</Button>
        </div>
      ) : null}

      {item.decisionNote ? <div className={css.qSavedNote}>{item.decisionNote}</div> : null}
      {!open || !props.draft || props.dismissPrompt ? null : <div className={css.qSavedNote}>{props.draft}</div>}

      {props.error ? <div className={css.qError}>{props.error}</div> : null}
    </div>
  )
}
